import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  ExclamationTriangleIcon, 
  DocumentMagnifyingGlassIcon, 
  ClockIcon,
  ArrowTrendingUpIcon,
  BellIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { listDocuments, getSCGRules } from '../services/api';
import ClassificationBadge from './ClassificationBadge';

export default function SmartRecommendations() {
  const { data: documentsData } = useQuery({
    queryKey: ['documents-for-recommendations'],
    queryFn: () => listDocuments(50),
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: scgData } = useQuery({
    queryKey: ['scg-for-recommendations'],
    queryFn: getSCGRules,
    refetchInterval: 300000, // Refresh every 5 minutes
  });

  // Analyze documents to generate smart recommendations
  const getRecommendations = () => {
    const recommendations = [];
    
    if (!documentsData?.documents) return recommendations;

    // Find documents needing review (low confidence)
    const lowConfidenceDocs = documentsData.documents.filter(doc => 
      doc.confidence && doc.confidence < 0.7
    );

    if (lowConfidenceDocs.length > 0) {
      recommendations.push({
        type: 'review',
        priority: 'high',
        title: 'Documents Need Review',
        description: `${lowConfidenceDocs.length} documents have low confidence scores`,
        count: lowConfidenceDocs.length,
        action: 'Review Documents',
        actionUrl: '/classify',
        icon: ExclamationTriangleIcon,
        color: 'text-amber-600 bg-amber-50 border-amber-200',
        iconColor: 'text-amber-500'
      });
    }

    // Check for classification inconsistencies
    const classificationGroups = {};
    documentsData.documents.forEach(doc => {
      const firstWords = doc.content?.split(' ').slice(0, 10).join(' ') || '';
      if (firstWords.length > 20) {
        if (!classificationGroups[firstWords]) {
          classificationGroups[firstWords] = [];
        }
        classificationGroups[firstWords].push(doc);
      }
    });

    const inconsistentGroups = Object.values(classificationGroups).filter(group => {
      if (group.length < 2) return false;
      const classifications = new Set(group.map(doc => doc.classification));
      return classifications.size > 1;
    });

    if (inconsistentGroups.length > 0) {
      const totalInconsistent = inconsistentGroups.reduce((sum, group) => sum + group.length, 0);
      recommendations.push({
        type: 'inconsistency',
        priority: 'medium',
        title: 'Classification Inconsistencies',
        description: `${totalInconsistent} similar documents have different classifications`,
        count: totalInconsistent,
        action: 'Review Similarities',
        actionUrl: '/classify',
        icon: DocumentMagnifyingGlassIcon,
        color: 'text-blue-600 bg-blue-50 border-blue-200',
        iconColor: 'text-blue-500'
      });
    }

    // Check for recent processing activity
    const recentDocs = documentsData.documents.filter(doc => {
      if (!doc.created_at) return false;
      const docDate = new Date(doc.created_at);
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      return docDate > hourAgo;
    });

    if (recentDocs.length > 0) {
      recommendations.push({
        type: 'activity',
        priority: 'low',
        title: 'Recent Activity',
        description: `${recentDocs.length} documents classified in the last hour`,
        count: recentDocs.length,
        action: 'View Recent',
        actionUrl: '/classify',
        icon: ClockIcon,
        color: 'text-green-600 bg-green-50 border-green-200',
        iconColor: 'text-green-500'
      });
    }

    // SCG Coverage Analysis
    if (scgData?.total_rules && documentsData?.total) {
      const rulesPerDoc = scgData.total_rules / Math.max(documentsData.total, 1);
      if (rulesPerDoc < 2) {
        recommendations.push({
          type: 'coverage',
          priority: 'medium',
          title: 'Limited SCG Coverage',
          description: `Only ${scgData.total_rules} SCG rules for ${documentsData.total} documents`,
          count: scgData.total_rules,
          action: 'Add SCG Rules',
          actionUrl: '/scg',
          icon: ChartBarIcon,
          color: 'text-purple-600 bg-purple-50 border-purple-200',
          iconColor: 'text-purple-500'
        });
      }
    }

    // Performance trends (simulated - could be real metrics)
    const avgProcessingTime = Math.random() * 5 + 2; // Simulated 2-7 seconds
    if (avgProcessingTime > 5) {
      recommendations.push({
        type: 'performance',
        priority: 'low',
        title: 'Processing Time High',
        description: `Average classification time: ${avgProcessingTime.toFixed(1)}s`,
        count: Math.round(avgProcessingTime),
        action: 'Check Settings',
        actionUrl: '/settings',
        icon: ArrowTrendingUpIcon,
        color: 'text-orange-600 bg-orange-50 border-orange-200',
        iconColor: 'text-orange-500'
      });
    }

    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const recommendations = getRecommendations();

  if (recommendations.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <BellIcon className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Smart Recommendations</h2>
        </div>
        <div className="text-center py-8">
          <BellIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">All Good!</h3>
          <p className="text-sm text-gray-500">
            No issues detected. Your classification system is running smoothly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <BellIcon className="w-5 h-5 text-blue-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Smart Recommendations</h2>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {recommendations.length} suggestion{recommendations.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      <div className="space-y-3">
        {recommendations.map((rec, index) => (
          <div key={index} className={`border rounded-lg p-4 ${rec.color}`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <rec.icon className={`w-5 h-5 ${rec.iconColor}`} />
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    {rec.title}
                  </p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                    rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {rec.priority}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {rec.description}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      {rec.count}
                    </span>
                    <span className="ml-1 text-sm text-gray-500">
                      {rec.type === 'activity' ? 'recent' : 
                       rec.type === 'coverage' ? 'rules' : 'items'}
                    </span>
                  </div>
                  <Link
                    to={rec.actionUrl}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    {rec.action}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          Recommendations update automatically based on system activity
        </p>
      </div>
    </div>
  );
}